<template>
  <div>
    <!-- Hero Section -->
    <Section>
      <div class="text-center max-w-4xl mx-auto">
        <h1 class="text-4xl md:text-5xl font-bold text-ink mb-4">
          Get in Touch
        </h1>
        <p class="text-xl text-muted">
          Ready to streamline your online retail management? Let us talk.
        </p>
      </div>
    </Section>

    <!-- Contact Form Section -->
    <Section background="gray">
      <div class="max-w-2xl mx-auto">
        <BaseCard>
          <form @submit.prevent="handleSubmit" class="space-y-6">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label for="name" class="block text-sm font-medium text-ink mb-2">
                  Name
                </label>
                <input
                  id="name"
                  v-model="form.name"
                  type="text"
                  required
                  placeholder="Your name"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
              
              <div>
                <label for="email" class="block text-sm font-medium text-ink mb-2">
                  Email
                </label>
                <input
                  id="email"
                  v-model="form.email"
                  type="email"
                  required
                  placeholder="your.email@company.com"
                  class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
                />
              </div>
            </div>

            <div>
              <label for="company" class="block text-sm font-medium text-ink mb-2">
                Company
              </label>
              <input
                id="company"
                v-model="form.company"
                type="text"
                placeholder="Your company name"
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent"
              />
            </div>

            <div>
              <label for="message" class="block text-sm font-medium text-ink mb-2">
                Message
              </label>
              <textarea
                id="message"
                v-model="form.message"
                rows="6"
                required
                placeholder="Tell us about your needs..."
                class="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand focus:border-transparent resize-none"
              ></textarea>
            </div>

            <!-- Error Message -->
            <div v-if="submitError" class="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {{ submitError }}
            </div>

            <!-- Success Message -->
            <div v-if="submitSuccess" class="p-4 bg-green-50 border border-green-200 rounded-lg text-green-700">
              Thank you for your message! We will get back to you as soon as possible.
            </div>

            <BaseButton
              type="submit"
              variant="primary"
              size="lg"
              class="w-full"
              :disabled="isSubmitting"
            >
              {{ isSubmitting ? 'Sending...' : 'Send Message' }}
            </BaseButton>
          </form>
        </BaseCard>
      </div>
    </Section>

    <!-- Contact Info -->
    <Section>
      <div class="text-center max-w-2xl mx-auto">
        <h2 class="text-2xl font-bold text-ink mb-8">
          Other Ways to Reach Us
        </h2>
        <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
          <!-- General Information -->
          <div class="flex flex-col items-center">
            <svg class="w-8 h-8 text-brand mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
            <p class="text-sm text-muted mb-2">General Information</p>
            <a href="mailto:Info@easytomanage.com" class="text-brand hover:text-blue-600 transition-colors font-medium">
              Info@easytomanage.com
            </a>
          </div>

          <!-- Support -->
          <div class="flex flex-col items-center">
            <svg class="w-8 h-8 text-brand mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
            <p class="text-sm text-muted mb-2">Support</p>
            <a href="mailto:Support@easytomanage.com" class="text-brand hover:text-blue-600 transition-colors font-medium">
              Support@easytomanage.com
            </a>
          </div>

          <!-- Sales -->
          <div class="flex flex-col items-center">
            <svg class="w-8 h-8 text-brand mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <p class="text-sm text-muted mb-2">Sales</p>
            <a href="mailto:Sales@easytomanage.com" class="text-brand hover:text-blue-600 transition-colors font-medium">
              Sales@easytomanage.com
            </a>
          </div>
        </div>

        <!-- Phone and Address -->
        <div class="mt-12 pt-8 border-t border-gray-200 space-y-6">
          <div class="flex items-center justify-center gap-3">
            <svg class="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
            <a href="tel:+12122732613" class="text-ink hover:text-brand transition-colors font-medium">
              (212) 273-2613
            </a>
          </div>

          <div class="flex items-center justify-center gap-3">
            <svg class="w-5 h-5 text-brand" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
            <span class="text-ink font-medium">
              681 Fifth Ave, 14th Floor, New York, NY 10022
            </span>
          </div>
        </div>
      </div>
    </Section>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'
import { useHead } from '@vueuse/head'
import { useI18n } from 'vue-i18n'
import Section from '@/components/Section.vue'
import BaseButton from '@/components/BaseButton.vue'
import BaseCard from '@/components/BaseCard.vue'
import { loadRecaptcha, submitContactForm } from '@/utils/formSubmission'

const { t } = useI18n()

// Initialize reCAPTCHA on component mount
onMounted(() => {
  loadRecaptcha().catch(error => console.warn('reCAPTCHA failed to load:', error))
})

useHead({
  title: () => `${t('contact.title')} - E2M`,
  meta: [
    { name: 'description', content: () => t('contact.subtitle') },
    { property: 'og:title', content: () => `${t('contact.title')} - E2M` },
    { property: 'og:description', content: () => t('contact.subtitle') }
  ]
})

const form = ref({
  name: '',
  email: '',
  company: '',
  message: ''
})

const isSubmitting = ref(false)
const submitSuccess = ref(false)
const submitError = ref('')

const handleSubmit = async () => {
  isSubmitting.value = true
  submitError.value = ''
  submitSuccess.value = false

  try {
    const result = await submitContactForm({
      name: form.value.name,
      email: form.value.email,
      company: form.value.company,
      message: form.value.message
    })

    if (result.success) {
      submitSuccess.value = true
      // Reset form
      form.value = {
        name: '',
        email: '',
        company: '',
        message: ''
      }
      // Hide success message after 5 seconds
      setTimeout(() => {
        submitSuccess.value = false
      }, 5000)
    } else {
      submitError.value = result.error || 'Failed to submit form'
    }
  } catch (error) {
    submitError.value = error instanceof Error ? error.message : 'An error occurred'
  } finally {
    isSubmitting.value = false
  }
}
</script>